import { type FC, forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import classNames from 'classnames/bind';
import { Theme } from '../types/enums';
import { FAIcon, IconStyle } from '../Icons/Icons';
import { type CheckboxPropsTypes, type CheckboxTheme } from './types/propsType';
import styles from './Checkbox.module.scss';
import { CheckboxVariant } from './types/enums';
const cx = classNames.bind(styles);

const Checkbox: FC<CheckboxPropsTypes> = forwardRef<HTMLInputElement, CheckboxPropsTypes>(
  (
    { text, disabled, checked = false, theme = Theme.BLACK, variant, subText, id, indeterminate = false, ...restProps },
    ref,
  ) => {
    const checkboxRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
      if (checkboxRef.current && indeterminate) {
        checkboxRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    useImperativeHandle(ref, () => checkboxRef.current!);

    function renderInputType() {
      return (
        <input
          id={`checkbox-${id}-${text}`}
          ref={checkboxRef}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          data-testid="checkbox-test-id"
          {...restProps}
        />
      );
    }

    const checkboxBorderClass = checked || indeterminate ? 'checkbox-border-filled' : 'checkbox-border-empty';

    const variantClassName = cx({
      [CheckboxVariant.DEFAULT]: variant !== CheckboxVariant.LONG_TEXT,
      [CheckboxVariant.LONG_TEXT]: variant === CheckboxVariant.LONG_TEXT,
    });

    const ClabelClassname = cx('checkbox', variantClassName, theme, checkboxBorderClass, { disabled: disabled });

    return (
      <label className={ClabelClassname} data-testid="checkbox-container-test-id">
        <section className={styles['checkbox-main-content']}>
          {renderInputType()}
          <span className={styles['checkbox-border']}>
            <FAIcon
              iconName={indeterminate ? 'minus' : checked ? 'check' : 'square'}
              iconStyle={checked || indeterminate ? IconStyle.SOLID : IconStyle.REGULAR}
              theme={theme}
              className={styles['checkmark']}
              enableHover={false}
            />
          </span>
          {text && (
            <label htmlFor={`checkbox-${id}-${text}`} className={styles['checkbox-content']}>
              <span className={styles['label']}>{text}</span>
            </label>
          )}
        </section>
        {subText && <span className={styles['label-details']}>{subText}</span>}
      </label>
    );
  },
);
Checkbox.displayName = 'Checkbox';
export default Checkbox;
export { CheckboxVariant };
export type { CheckboxTheme };
